import { Link} from 'react-router-dom';
import { useEffect, useState, useRef } from 'react';
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import '../../styles/components/pages/student-dashboard.css';
import { useUser } from "../../contexts/UserContext";
import { useCourse } from "../../contexts/CourseContext";
import TopicSidebar from '../../components/topicsBar';
import { questionService, courseService, hintService, errorsService, authService, submissionService, llmService, analyticsService } from '../../services/api';
import { toast } from 'react-toastify';
import { useAuth } from "../../contexts/AuthContext";
import { useLocation } from 'react-router-dom';
import DashboardChatbot from './dashboardChatbot';


export default function StudentHome() {
  {/*all useState hooks*/}
  {/* useState used when value affects what's shown on screen, re-renders when there's a change */}
  {/*const [state, setState] = useState(initialValue);*/}
  const [greeting, setGreeting] = useState('')
  const [showCoursesModal, setShowCoursesModal] = useState(false);
  const [joinCode, setJoinCode] = useState("");
  const [joinError, setJoinError] = useState("");
  const [joinLoading, setJoinLoading] = useState(false);

  const [courseCompletedCount, setCourseCompletedCount] = useState(0);
  const [allHints, setAllHints] = useState([]);

  const [totalCourseProblems, setTotalCourseProblems] = useState(0);
  const [longestStreak, setLongestStreak] = useState(0);
  const [weeklyCount, setWeeklyCount] = useState(0);
  const [lastWeekCount, setLastWeekCount] = useState(0);  
  const [mostRecentFunctionName, setMostRecentFunctionName] = useState(null);

  const [hintMedianSeconds, setHintMedianSeconds] = useState(null);
  const [reflectionSubmitted, setReflectionSubmitted] = useState(false);
  const [reflectionText, setReflectionText] = useState('');
  const [totalHintsUsed, setTotalHintsUsed] = useState(0);
  const [hintConceptSummary, setHintConceptSummary] = useState(null);
  const [hintSummaryLoading, setHintSummaryLoading] = useState(false);
  const [summaryRating, setSummaryRating] = useState(null);
  
  const [errorTopics, setErrorTopics] = useState([]);
  const [incompleteProblems, setIncompleteProblems] = useState([]);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [topicErrors, setTopicErrors] = useState([]);
  const [currentErrorIdx, setCurrentErrorIdx] = useState(0);
  const [showErrorPopup, setShowErrorPopup] = useState(false);

  const [congratsMessage, setCongratsMessage] = useState(null);
  const [showHintHistory, setShowHintHistory] = useState(false);
  const [hasCompletedAnySets, setHasCompletedAnySets] = useState(null);
  


  {/*all useRef hooks*/}
  {/* useRef used when value is internal bookkeeping, doesn't cause a re-render */}
  const hintSummaryFetchingRef = useRef(false);
  const sessionClickCount = useRef(0);
  const prevCRN = useRef(null);


  {/*all context hooks*/}
  {/*a context is shared state that lives outside any single component*/}
  const { showJoinModal, openJoinModal, closeJoinModal, coursesInitialized } = useCourse();
  const { login } = useAuth();
  const location = useLocation();
  const { user, roleData} = useUser();
  const { courses, setCourses, selectedCourse, selectCourse } = useCourse();



  function logEvent(eventType, details = '') {
    if ((user?.studentID && selectedCourse?.CRN && roleData === 'student') || 
      user?.studentID === 394179418) {
      if (!['dashboard_session'].includes(eventType)) {
        sessionClickCount.current += 1;
      }
      analyticsService.logEvent(user.studentID, selectedCourse.CRN, eventType, details);
    }
  }


  {/*async fetch functions*/}
  {/* functions that fetch data from the backend... async because they await API calls*/}

  async function handleJoinCourse(e) {
    e.preventDefault();
    setJoinLoading(true);
    setJoinError("");
    try {
      // Omit role so the server uses whichever code matched (student / instructor / TA).
      // Sending role: 'student' would downgrade an instructor join code to student-only.
      const joinedCourses = await courseService.joinByCode({ code: joinCode, gtid: user.gtid });
      closeJoinModal();
      setJoinCode("");
      setJoinError("");
      setCourses(joinedCourses || []);
      toast.success("Successfully joined course! Refreshing session...");
      
      // Force a seamless re-authentication to get fresh token with updated role
      try {
        // Clear current session
        await authService.logout();
        
        // Small delay to ensure logout completes
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Trigger OAuth login flow - user will be redirected back with new token
        await login();
      } catch (err) {
        console.error('Error during re-authentication:', err);
        toast.error("Please log in again to see updated role");
      }
    } catch (error) {
      let msg = "Network error. Please try again.";
      if (error && error.message) {
        if (error.message.includes("404")) {
          msg = "Invalid course code. Please check and try again.";
        } else {
          const parts = error.message.split(":");
          msg = parts.length > 1 ? parts[parts.length - 1].trim() : error.message;
        }
      }
      setJoinError(msg);
      toast.error(msg);
    } finally {
      setJoinLoading(false);
    }
  }
  
  async function fetchIncompleteProblems() {
    if (!user?.studentID || !selectedCourse?.CRN) return;
    try {
      const data = await questionService.getIncompleteQuestionsByIdAndCourse(
        user.studentID, selectedCourse.CRN
      );
      const problems = data
        .map(q => ({
          qid: q.questionID,
          functionName: q.functionName,
          topic: q.topic,
          attempts: q.attempts,
          lastTried: q.lastAttempt 
            ? new Date(q.lastAttempt).toLocaleDateString('en-US', {month: 'short', day: 'numeric'})
            : 'never'
        }))
        .filter(p => p.attempts >= 2)
        .reduce((acc, problem) => {
          const existing = acc.find(p => p.functionName === problem.functionName);
          if (!existing || problem.attempts > existing.attempts) {
            return [...acc.filter(p => p.functionName !== problem.functionName), problem];
          }
          return acc;
        }, [])
        .sort((a, b) => b.attempts - a.attempts)
        .slice(0, 3);
      setIncompleteProblems(problems);
    } catch (error) {
      console.error("Error fetching incomplete problems:", error);
    }
  }

  async function fetchCourseCompletedCount() {
    if (!user?.studentID || !selectedCourse?.CRN) return;
    try {
      const data = await questionService.getCompletedQuestionsByIdAndCourse(user.studentID, selectedCourse.CRN);
      console.log("course completed:", data, "totalCourseProblems:", totalCourseProblems);
      setCourseCompletedCount(data.length || 0);
    } catch (error) {
      console.error("Error fetching course completed count:", error);
    }
  }

  async function checkShouldRelock() {
    console.log("checkShouldRelock — user:", user?.studentID, "CRN:", selectedCourse?.CRN);
    if (!user?.studentID || !selectedCourse?.CRN) return;
    
    const hasReflected = localStorage.getItem(`reflection_${selectedCourse.CRN}`) === 'true';

    try {
      const setsData = await questionService.getCompletedProblemSets(user.studentID, selectedCourse.CRN);
    
      const currentSets = setsData?.completed_problem_sets || [];
      setHasCompletedAnySets(currentSets.length > 0);
  
      if (!hasReflected) {
        if (currentSets.length > 0) {
          const mostRecentSet = currentSets[currentSets.length - 1];
          const msg = `🎉 You completed ${mostRecentSet}! Unlock your updated dashboard below.`;
          setCongratsMessage(msg);
          localStorage.setItem(`congratsMessage_${selectedCourse.CRN}`, msg);
        }
        return;
      }
          
      const savedSets = JSON.parse(localStorage.getItem(`reflectionCompletedSets_${selectedCourse.CRN}`) || '[]');
      const newSets = currentSets.filter(s => !savedSets.includes(s));

      console.log("currentSets:", currentSets, "savedSets:", savedSets, "newSets:", newSets);

      if (newSets.length > 0) {
        // relock — new set completed
        localStorage.removeItem(`reflection_${selectedCourse.CRN}`);
        localStorage.removeItem(`summary_${selectedCourse.CRN}`);
        localStorage.removeItem(`summaryRating_${selectedCourse.CRN}`);
        localStorage.removeItem(`reflectionText_${selectedCourse.CRN}`);
        hintSummaryFetchingRef.current = false;
        setReflectionSubmitted(false);
        setReflectionText('');
        setHintConceptSummary(null);
        setSummaryRating(null);
        setCongratsMessage(`🎉 You completed ${newSets.join(', ')}! Reflect to unlock the changes to your dashboard below.`);
        localStorage.setItem(`congratsMessage_${selectedCourse.CRN}`, `🎉 You completed ${newSets.join(', ')}! Reflect to unlock the changes to your dashboard below.`);
        logEvent('relock_triggered', newSets.join(','));
      } else {
        // no new sets
        console.log("no new sets, clearing congrats");
        setCongratsMessage(null);
        localStorage.removeItem(`congratsMessage_${selectedCourse.CRN}`);
      }
      }
     catch (error) {
      console.error("Error checking relock:", error);
    }
  }

  async function fetchAllHints() {
    if (!user?.studentID || !selectedCourse?.CRN) return;
    try {
      const data = await hintService.getAllHintsByStudentAndCourse(user.studentID, selectedCourse.CRN);
      setAllHints(data || []);
    } catch (error) {
      console.error("Error fetching all hints:", error);
    }
  }

  async function fetchTotalCourseProblems() {
    if (!selectedCourse?.CRN) return;
    try {
      const data = await questionService.getProblemsFromCourse(selectedCourse.CRN);
      setTotalCourseProblems(data.length || 0);
    } catch (error) {
      console.error("Error fetching total course problems:", error);
    }
  }

  async function fetchHintsUsed() {
    if (!user?.studentID || !selectedCourse?.CRN) {
      setTotalHintsUsed(0);
      return;
    }
    try {
      const response = await hintService.getTotalHintsByStudentAndCourse(user.studentID, selectedCourse.CRN);
      setTotalHintsUsed(response.total_hints || 0);
    } catch (error) {
      console.error("Error in fetching hints used:", error);
      setTotalHintsUsed(0);
    }
  }

  async function fetchHintMedian() {
    if (!user?.studentID || !selectedCourse?.CRN) return;
    try {
      const data = await hintService.getMedianTimeBetweenHints(user.studentID, selectedCourse.CRN);
      setHintMedianSeconds(data.median_seconds || null);
    } catch (error) {
      console.error("Error fetching hint median:", error);
    }
  }

  async function fetchHintConceptSummary() {
    console.log("fetchHintConceptSummary — user:", user?.studentID, "CRN:", selectedCourse?.CRN);
    if (!user?.studentID || !selectedCourse?.CRN) return;
    if (hintSummaryFetchingRef.current) return;
    // check cache first
    const cached = localStorage.getItem(`summary_${selectedCourse?.CRN}`);
    if (cached) {
      setHintConceptSummary(cached);
      return;
    }
    hintSummaryFetchingRef.current = true;
    try {
      setHintSummaryLoading(true);
      const data = await llmService.getHintConceptSummary(user.studentID, selectedCourse.CRN);
      if (data.enough_data && data.summary) {
        setHintConceptSummary(data.summary);
        localStorage.setItem(`summary_${selectedCourse?.CRN}`, data.summary);
      } else {
        setHintConceptSummary(null);
      }
    } catch (error) {
      console.error("Error fetching hint concept summary:", error);
    } finally {
      setHintSummaryLoading(false);
      hintSummaryFetchingRef.current = false;
    }
  }
  
  async function fetchWeeklySubmissions() {
    if (!user?.studentID || !selectedCourse?.CRN) {
      setWeeklyCount(0);
      setLastWeekCount(0);
      setLongestStreak(0);
      return;
    }
    try {
      const [data, courseQuestions] = await Promise.all([
        submissionService.getSubmissionsByStudentId(user.studentID),
        questionService.getProblemsFromCourse(selectedCourse.CRN)
      ]);
      const courseQidSet = new Set(courseQuestions.map(q => q.questionID));
      const courseData = data.filter(s => courseQidSet.has(s.qid));
      const now = new Date();
      const startOfThisWeek = new Date(now);
      startOfThisWeek.setDate(now.getDate() - now.getDay());
      startOfThisWeek.setHours(0, 0, 0, 0);
      const startOfLastWeek = new Date(startOfThisWeek);
      startOfLastWeek.setDate(startOfThisWeek.getDate() - 7);
      const thisWeek = [...new Set(
        courseData.filter(s =>
          s.status === 'Passed' && new Date(s.timestamp) >= startOfThisWeek
        ).map(s => s.qid)
      )].length;
      const lastWeek = [...new Set(
        courseData.filter(s =>
          s.status === 'Passed' &&
          new Date(s.timestamp) >= startOfLastWeek &&
          new Date(s.timestamp) < startOfThisWeek
        ).map(s => s.qid)
      )].length;
      setWeeklyCount(thisWeek);
      setLastWeekCount(lastWeek);
      const submissionDays = [...new Set(
        courseData
          .filter(s => s.status === 'Passed')
          .map(s => new Date(s.timestamp).toDateString())
      )].map(d => new Date(d)).sort((a, b) => a - b);
      let streak = 0;
      let maxStreak = 0;
      for (let i = 0; i < submissionDays.length; i++) {
        if (i === 0) {
          streak = 1;
        } else {
          const diff = (submissionDays[i] - submissionDays[i-1]) / (1000 * 60 * 60 * 24);
          streak = diff === 1 ? streak + 1 : 1;
        }
        maxStreak = Math.max(maxStreak, streak);
      }
      setLongestStreak(maxStreak);
      const mostRecent = courseData
        .filter(s => s.status === 'Passed')
        .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))[0];
      if (mostRecent?.qid) {
        try {
          const question = await questionService.getProblem(mostRecent.qid);
          setMostRecentFunctionName(question.functionName || null);
        } catch (e) {
          setMostRecentFunctionName(null);
        }
      }
    } catch (error) {
      console.error("Error fetching weekly submissions:", error);
      setWeeklyCount(0);
      setLastWeekCount(0);
    }
  }

  async function fetchErrorTopics() {
    if (!user?.studentID) return;
    try {
      const data = await errorsService.getAllErrorsForStudent(user.studentID, selectedCourse?.CRN);
      setErrorTopics(data.topics || []);
    } catch (error) {
      console.error("Error fetching error topics:", error);
    }
  }

  async function openTopicPopup(topic) {
    setSelectedTopic(topic);
    setCurrentErrorIdx(0);
    setShowErrorPopup(true);
    try {
      const data = await errorsService.getErrorsWithCodeForStudent(user.studentID, topic.topic, selectedCourse.CRN);
      setTopicErrors(data || []);
    } catch (error) {
      console.error("Error fetching topic errors:", error);
      setTopicErrors([]);
    }
  }



  {/*initialization useEffect hooks*/}
  useEffect(() => {
    if (courses && courses.length > 0 && !selectedCourse) {
      selectCourse(courses[0]);
    }
  }, [courses, selectedCourse, selectCourse]);

  useEffect(() => {
    const currentHour = new Date().getHours()
    if (currentHour < 12) setGreeting('Good morning')
    else if (currentHour < 18) setGreeting('Good afternoon')
    else setGreeting('Good evening')
  }, [])

  useEffect(() => {
    if (!user?.studentID || !selectedCourse?.CRN) return;
    const hasReflected = localStorage.getItem(`reflection_${selectedCourse.CRN}`) === 'true';
    const hasCachedSummary = localStorage.getItem(`summary_${selectedCourse.CRN}`);
    Promise.all([
      fetchHintsUsed(),
      fetchWeeklySubmissions(),
      fetchErrorTopics(),
      fetchTotalCourseProblems(),
      fetchAllHints(),
      fetchCourseCompletedCount(),
      fetchIncompleteProblems(),
      fetchHintMedian(),
      checkShouldRelock()
    ]).then(() => {
      const hasCachedSummaryNow = localStorage.getItem(`summary_${selectedCourse.CRN}`);
      if (!hasCachedSummaryNow) {
        fetchHintConceptSummary();
      }
    });
  }, [user?.studentID, selectedCourse?.CRN]);

  useEffect(() => {
    if (user && roleData === 'undecided' && Array.isArray(courses) && courses.length === 0 && !showJoinModal) {
      openJoinModal();
    }
  }, [user, roleData, courses, showJoinModal, openJoinModal]);

  useEffect(() => {
    async function fetchCourses() {
      if (!user) return;
      if (courses && courses.length > 0) return;
      if (courses && courses.length > 0) console.log("Fetching courses end");
      try {
        console.log("Fetching courses effect running", { user, courses });
        const data = await courseService.getCoursesForUsername(user.loginID);
        setCourses(data);
        if (data.length === 1) {
          console.log("Auto-selecting the only available course.");
          selectCourse(data[0]);
        }
        console.log(selectedCourse);
      } catch (error) {
        console.error("Error fetching courses:", error);
        setCourses([]);
      }
    }
    fetchCourses();
  }, [user]);

  useEffect(() => {
    if (prevCRN.current === selectedCourse?.CRN) return;
  
    prevCRN.current = selectedCourse?.CRN;
    setWeeklyCount(0);
    setLastWeekCount(0);
    setErrorTopics([]);
    setSelectedTopic(null);
    setShowErrorPopup(false);
    setTotalCourseProblems(0);
    setLongestStreak(0);
    setHintMedianSeconds(null);
    setHasCompletedAnySets(null);
    setReflectionSubmitted(false);
    setReflectionText('');
    setSummaryRating(null);
    setMostRecentFunctionName(null);
    setHintConceptSummary(null);
    setHintSummaryLoading(false);
    hintSummaryFetchingRef.current = false;
    setCourseCompletedCount(0);
    setShowHintHistory(false);
    setCongratsMessage(null);
    setIncompleteProblems([]);
    setAllHints([]);
  
    if (selectedCourse?.CRN) {
      const savedSummary = localStorage.getItem(`summary_${selectedCourse.CRN}`);
      const savedReflection = localStorage.getItem(`reflection_${selectedCourse.CRN}`);
      const savedRating = localStorage.getItem(`summaryRating_${selectedCourse.CRN}`);
      const savedReflectionText = localStorage.getItem(`reflectionText_${selectedCourse.CRN}`);

      if (savedSummary) {
        setHintConceptSummary(savedSummary);
        hintSummaryFetchingRef.current = true;
      }
      if (savedReflection === 'true') setReflectionSubmitted(true);
      if (savedRating) setSummaryRating(savedRating);
      if (savedReflectionText) setReflectionText(savedReflectionText);
    }
  }, [selectedCourse?.CRN]);
  
  useEffect(() => {
    if (!user?.studentID || !selectedCourse?.CRN) return;
    let activeTime = 0;
    let lastActiveStart = Date.now();
    let hiddenAt = 0;
    let sessionLogged = false;
    const logSession = () => {
    if (sessionLogged) return;
    if (activeTime > 10) {
      sessionLogged = true;
      logEvent('dashboard_session', `${activeTime}s clicks:${sessionClickCount.current}`);
      sessionClickCount.current = 0;
    }
  };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        activeTime += Math.round((Date.now() - lastActiveStart) / 1000);
        hiddenAt = Date.now();
      } else {
        const awaySeconds = Math.round((Date.now() - hiddenAt) / 1000);
        if (awaySeconds > 10) { // away for more than 10 seconds and its a new session
          logSession();
          activeTime = 0;
          sessionLogged = false;
        }
        lastActiveStart = Date.now();
      }
    };
    const handleBeforeUnload = () => {
      activeTime += Math.round((Date.now() - lastActiveStart) / 1000);
      logSession();
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      activeTime += Math.round((Date.now() - lastActiveStart) / 1000);
      logSession();
    };
  }, [selectedCourse?.CRN, user?.studentID, location.pathname]);



  {/* initialization constants*/}
  const courseCount = Array.isArray(courses) ? courses.length : 0;

  {/* your wins constants*/}
  const percentComplete = totalCourseProblems > 0 
  ? (() => {
      const raw = (courseCompletedCount / totalCourseProblems) * 100;
      return raw < 1 ? Math.round(raw * 10) / 10 : Math.round(raw);
    })()
  : 0;

    {/* Something to figure out later, readding this comparison to last week feature.
  const weeklyArrow = weeklyCount > lastWeekCount ? '↑' : weeklyCount < lastWeekCount ? '↓' : '';
  
  const weeklyIcon = weeklyCount === 0 ? (
    <svg className="stat-card-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M8 12h8"/>
    </svg>
  ) : weeklyCount > lastWeekCount ? (
    <svg className="stat-card-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"/>
    </svg>
  ) : weeklyCount < lastWeekCount ? (
    <svg className="stat-card-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M13 17h8m0 0v-8m0 8l-8-8-4 4-6-6"/>
    </svg>
  ) : (
    <svg className="stat-card-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M5 12h14"/>
    </svg>
  );

  const weeklySubtitle = weeklyCount === 0 
  ? "No problems completed yet — let's change that!"
  : weeklyCount > lastWeekCount 
  ? `Up from last week — you're on a roll!`
  : weeklyCount < lastWeekCount 
  ? `Down from last week — get back at it!`
  : `Matching last week's count — can you beat it?`;
    */}

  {/* errors constants */}
  const dialColors = ['#378ADD', '#9FE1CB', '#AFA9EC', '#C0DD97', '#F0C675', '#B4B2A9'];
  let offset = 0;
  const CIRCUMFERENCE = 2 * Math.PI * 85;
  const otherPercentage = errorTopics.slice(5).reduce((sum, t) => sum + t.percentage, 0);
  
  const dialSlices = errorTopics.slice(0, 5).map((topic, i) => {
    const dash = (topic.percentage / 100) * CIRCUMFERENCE;
    const slice = { ...topic, dash, offset: -offset, color: dialColors[i] };
    offset += dash;
    return slice;
  });

  if (dialSlices.length > 0) {
    const usedDash = dialSlices.reduce((sum, s) => sum + s.dash, 0);
    dialSlices[dialSlices.length - 1].dash += (CIRCUMFERENCE - usedDash);
  }
  
  if (otherPercentage > 0) {
    const dash = (otherPercentage / 100) * CIRCUMFERENCE;
    dialSlices.push({
      topic: 'Other',
      percentage: otherPercentage,
      count: errorTopics.slice(5).reduce((sum, t) => sum + t.count, 0),
      dash,
      offset: -offset,
      color: dialColors[5]
    });
  }
  
  {/* hints constants */}
  const hintSessions = allHints.reduce((sessions, hint, i) => {
    const prev = allHints[i - 1];
    const timeDiff = prev
      ? (new Date(hint.timestamp) - new Date(prev.timestamp)) / 1000
      : null;
    const sameQuestion = prev?.qid === hint.qid;
  
    if (i === 0 || timeDiff > 3600) {
      sessions.push({ hints: [], date: new Date(hint.timestamp + 'Z') });
    }
    const currentSession = sessions[sessions.length - 1];
    const prevInSession = currentSession.hints[currentSession.hints.length - 1];
    const gapSeconds = prevInSession && prevInSession.qid === hint.qid
      ? Math.round((new Date(hint.timestamp) - new Date(prevInSession.timestamp)) / 1000)
      : null;
    currentSession.hints.push({ ...hint, gapSeconds });
    return sessions;
  }, []).map(session => {
    const gaps = session.hints.map(h => h.gapSeconds).filter(g => g !== null);
    gaps.sort((a, b) => a - b);
    const mid = Math.floor(gaps.length / 2);
    const median = gaps.length === 0 ? null : gaps.length % 2 !== 0 ? gaps[mid] : (gaps[mid - 1] + gaps[mid]) / 2;
    const sessionColor = median === null ? '#6b7280' : median < 60 ? '#dc2626' : '#16a34a';
    return { ...session, median, sessionColor };
  }).filter(session => session.median !== null).reverse();

  const recentWeightedMedian = (() => {
    if (hintSessions.length === 0) return null;
    let weightedSum = 0;
    let totalWeight = 0;
    hintSessions.forEach((session, i) => {
      if (session.median === null) return;
      const weight = Math.pow(0.5, i);
      weightedSum += session.median * weight;
      totalWeight += weight;
    });
    return totalWeight === 0 ? null : weightedSum / totalWeight;
  })();  

  const hintMessage = recentWeightedMedian === null 
  ? null
  : recentWeightedMedian < 60
  ? "You're requesting hints quickly — make sure you work through each one before moving on"
  : "You're engaging thoughtfully with hints — keep reflecting before requesting the next one";

  const [hoveredButton, setHoveredButton] = useState('up');

  const buttonBaseStyle = {
      flex: 1,
      padding: '14px',
      fontSize: '16px',
      fontWeight: '600',
      borderRadius: '8px',
      cursor: 'pointer',
      outline: 'none',
      WebkitTapHighlightColor: 'transparent',
      transition: 'background 0.15s ease, color 0.15s ease, border-color 0.15s ease',
  };

  return (
    <>
      <div className="student-dashboard">

        <div id="sidebar">
          <TopicSidebar />
        </div> {/* sidebar div*/}

        <div className="student-dashboard-content">
              <div className="student-dashboard-container">


                <div className="course-header-row">
                    <h1 className="student-dashboard-heading">{greeting}, {user?.name || 'Student'}</h1>
                    <div className="quick-actions-buttons">
                          <Link to="/previousQs" className="quick-action-button">Your Previous Submissions</Link>
                          <Button className="quick-action-button" onClick={openJoinModal}>Join a Course</Button>
                          <Button className="quick-action-button" onClick={() => setShowCoursesModal(true)}>View Enrolled Courses</Button>
                    </div>
                </div> {/* course header row div*/}


                <div className="student-stats-grid">

                    <div className="stat-card">
                        <div className="stat-card-header">
                            <span className="stat-card-label">Current Course</span>
                            <svg className="stat-card-icon" viewBox="0 0 24 24">
                              <path d="M12 14l9-5-9-5-9 5 9 5z" />
                              <path d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" />
                            </svg>
                        </div> {/* stat card header div*/}
                        <div className="stat-card-value">{selectedCourse?.name}</div>
                        <div className="stat-card-subtitle">{selectedCourse?.semester || 'No semester info'}</div>
                    </div> {/* 1 stat card div*/}

                <div className="stat-card">
                    <div className="stat-card-header">
                        <span className="stat-card-label"> Hints Used</span>
                        <svg className="stat-card-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M9 18h6"/>
                            <path d="M10 22h4"/>
                            <path d="M15.09 14c.18-.98.65-1.74 1.41-2.5A4.65 4.65 0 0018 8 6 6 0 006 8c0 1 .23 2.23 1.5 3.5A4.61 4.61 0 018.91 14"/>
                        </svg>
                    </div> {/* stat card header div*/}
                  <div className="stat-card-value">{totalHintsUsed}</div>
                </div> {/* 1 stat card div*/}

                <div className="stat-card">
                    <div className="stat-card-header">
                        <span className="stat-card-label"> Problems Completed</span>
                        <svg className="stat-card-icon" viewBox="0 0 24 24">
                            <path d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                        </svg>
                    </div> {/* stat card header div*/}
                  <div className="stat-card-value">{courseCompletedCount}</div>
                </div> {/* 1 stat card div*/}

                </div> {/* student stats grid div*/}

            {/* IMPORTANT: quick-actions styling is now being used for the entire StudentDashboard section!! */}
              <div className="quick-actions">

                  <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start'}}>
                      <h2 className="quick-actions-heading">Student Dashboard</h2>     
                      <div style={{textAlign: 'right'}}>
                          <Link to="/knowledge-components" className="quick-action-button">
                            Your Overall Mastery Breakdown
                          </Link>
                          <p style={{fontSize: '.875rem', color: '#6b7280', marginTop: '0.5rem'}}>
                            Uses Knowledge Component (KC) vectors to estimate your skill levels.
                          </p>
                    </div>
                  </div> {/* top of student dashboard div*/}

                  {congratsMessage && (!reflectionSubmitted || !congratsMessage.includes('Reflect')) && (
                    <div style={{
                      background: '#f0fdf4',
                      border: '1px solid #16a34a',
                      borderRadius: '12px',
                      padding: '1rem 1.25rem',
                      marginBottom: '1rem',
                      color: '#003057',
                      fontWeight: '600',
                      fontSize: '17px'
                    }}>
                      {congratsMessage} {/* congrats message, null if no congrats*/} 
                    </div> 
                  )}

                  <div className="wins-section">
                    <h3 className="dash-section-heading">Your wins</h3>
                    <p className="dash-section-sub">Small steps, big progress</p>

                    <div className="wins-grid">

                        <div className="win-card">
                          <div className="win-emoji">🔥</div>
                          <div className="win-title">{weeklyCount} {weeklyCount === 1 ? 'problem' : 'problems'} this week</div>
                          <div className="win-desc">
                            {weeklyCount === 0 ? 'Start solving to build your streak!' : weeklyCount >= 5 ? 'Incredible week of practice!' : 'Solid week of practice so far'}
                          </div>
                        </div> {/* 1 win card */}

                      <div className="win-card">
                          <div className="win-emoji">📊</div>
                          <div className="win-title">{percentComplete}% of available problems complete</div>
                          <div className="win-desc">
                          {percentComplete === 100 ? 'You completed all available problems — incredible!' : percentComplete === 0 ? 'Start solving to track your progress!' : percentComplete >= 75 ? 'Almost there — amazing progress!' : percentComplete >= 50 ? 'Over halfway through the material!' : `${courseCompletedCount} of ${totalCourseProblems} problems have been solved`}
                          </div>
                      </div> {/* 1 win card */}

                      <div className="win-card">
                          <div className="win-emoji">📅</div>
                          <div className="win-title">Longest streak: {longestStreak} {longestStreak === 1 ? 'day' : 'days'}</div>
                          <div className="win-desc">
                            {longestStreak === 0 ? 'Start solving daily to build a streak!' : longestStreak >= 7 ? 'A week straight — incredible consistency!' : longestStreak >= 3 ? 'Great consistency — keep it up!' : 'Building your streak — keep coming back!'}
                          </div>
                      </div> {/* 1 win card */}

                      <div className="win-card">
                          <div className="win-emoji">⭐</div>
                          <div className="win-title">
                            {mostRecentFunctionName ? `Last solved: ${mostRecentFunctionName}()` : 'Solve your first problem!'}
                          </div>
                          <div className="win-desc">
                            {mostRecentFunctionName 
                              ? 'Nice work!' 
                              : 'Your most recently solved problem will appear here'}
                          </div>
                      </div> {/* 1 win card */}

                    </div> {/* wins grid div*/}
                  </div> {/* wins section div*/}
                    
                    {/* Reflection gate — wraps everything below */}
                    {hasCompletedAnySets === null ? null : (!reflectionSubmitted && hasCompletedAnySets) ? (
                      <div style={{position: 'relative'}}>
                        
                    {/* Blurred preview — actual dashboard content */}
                    <div style={{
                      filter: 'blur(3px)',
                      pointerEvents: 'none',
                      userSelect: 'none',
                      opacity: 0.7,
                    }}>
                      {/* Error dial section */}
                        <div className="error-dial-section">
                          <h2 className="dash-section-heading">Learn from your Error History</h2>
                          <p className="dash-section-sub">Tap a topic to see your previous incorrect submissions</p>
                          <div className="error-dial-wrap">
                          <div style={{width: '200px', height: '200px', borderRadius: '50%', background: 'conic-gradient(#378ADD 0% 40%, #9FE1CB 40% 65%, #AFA9EC 65% 80%, #C0DD97 80% 100%)', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                            <div style={{width: '100px', height: '100px', borderRadius: '50%', background: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column'}}>
                              <div style={{fontSize: '13px', fontWeight: '700', color: '#003057'}}>click on</div>
                              <div style={{fontSize: '13px', fontWeight: '700', color: '#003057'}}>a slice</div>
                            </div>
                          </div>
                            <div style={{flex: 1, display: 'flex', flexDirection: 'column', gap: '0.5rem'}}>
                              {[['#378ADD', 'Topic A', '40%'], ['#9FE1CB', 'Topic B', '25%'], ['#AFA9EC', 'Topic C', '15%']].map(([color, label, pct], i) => (
                                <div key={i} style={{height: '36px', background: '#f9fafb', borderRadius: '8px', display: 'flex', alignItems: 'center', padding: '0 0.75rem', gap: '0.5rem'}}>
                                  <span style={{width: '10px', height: '10px', borderRadius: '50%', background: color, flexShrink: 0}}></span>
                                  <span style={{fontSize: '13px', color: '#374151', fontWeight: '500'}}>{label}</span>
                                  <span style={{marginLeft: 'auto', fontSize: '13px', color: '#6b7280'}}>{pct}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>

                          {/* Hint insights section */}
                          <div className="error-dial-section">
                            <h2 className="dash-section-heading">Your Hint Insights</h2>
                            <p className="dash-section-sub">Personalized feedback based on how you use hints</p>
                            <div className="hint-card" style={{marginBottom: '0.75rem'}}>
                              <span className="hint-concepts-emoji">🤔</span>
                              <div>
   
                                <div className="hint-message">A note on your hint usage...</div>
                              </div>
                            </div>
                            <div style={{display: 'flex', flexDirection: 'column', gap: '4px', padding: '0.5rem 0'}}>
                              {[['#16a34a', '2m 30s'], ['#16a34a', '45s'], ['#dc2626', '1m 10s']].map(([color, gap], i) => (
                                <div key={i} style={{height: '28px', background: '#f9fafb', borderRadius: '6px', borderLeft: `3px solid ${color}`, display: 'flex', alignItems: 'center', padding: '0 0.75rem'}}>
                                  <span style={{fontSize: '12px', color: '#6b7280'}}>↕ {gap}</span>
                                </div>
                              ))}
                            </div>
                          </div>

                          {/* AI summary section */}
                          <div className="error-dial-section">
                            <div className="win-card hint-concepts-card">
                              <div style={{flex: 1}}>
                                <div className="win-emoji">🤖</div>
                                <div className="win-title">AI Suggested Focus Areas</div>
                                <div style={{marginTop: '0.5rem', display: 'flex', flexDirection: 'column', gap: '0.4rem'}}>
                                  <div style={{height: '16px', background: '#e5e7eb', borderRadius: '4px', width: '90%'}}></div>
                                  <div style={{height: '16px', background: '#e5e7eb', borderRadius: '4px', width: '75%'}}></div>
                                  <div style={{height: '16px', background: '#e5e7eb', borderRadius: '4px', width: '85%'}}></div>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* What's next section */}
                          <div className="error-dial-section">
                            <h2 className="dash-section-heading">What's Next?</h2>
                            <p className="dash-section-sub">Problems worth revisiting based on your attempt history</p>
                            {[['calculateTotal()', '5 attempts'], ['findMax()', '3 attempts'], ['sortList()', '2 attempts']].map(([fn, attempts], i) => (
                              <div key={i} className="win-card" style={{display: 'flex', alignItems: 'center', gap: '1rem', padding: '0.75rem 1rem', marginBottom: '0.5rem'}}>
                                <div style={{background: 'white', borderRadius: '8px', padding: '6px 10px', textAlign: 'center', minWidth: '52px'}}>
                                  <div style={{fontSize: '1.2rem', fontWeight: '700', color: '#003057'}}>{attempts.split(' ')[0]}</div>
                                  <div style={{fontSize: '13px', color: '#003057'}}>attempts</div>
                                </div>
                                <div style={{flex: 1}}>
                                  <div className="win-title">{fn}</div>
                                  <div className="win-desc">Last tried recently</div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>

                    {/* Lock icon overlay */}
                    <div style={{
                      position: 'absolute',
                      top: 0, left: 0, right: 0, bottom: 0,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'flex-start',
                      paddingTop: '2rem',
                      background: 'rgba(255,255,255,0.45)',
                      borderRadius: '12px',
                    }}>
                      <div style={{fontSize: '4rem', marginBottom: '0.5rem'}}>🔒</div>

                      {/* Reflection form sits inside the overlay */}
                      <div style={{
                        background: 'white',
                        borderRadius: '12px',
                        padding: '1.25rem',
                        border: '1px solid #d1d5db',
                        width: '90%',
                        maxWidth: '600px',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                      }}>
                        <p className="feedback-question">Before we share your dashboard, what do you think you are struggling with the most?</p>
                        <textarea
                          value={reflectionText}
                          onChange={e => setReflectionText(e.target.value)}
                          placeholder="Write your thoughts here..."
                          maxLength={200}
                          style={{width: '100%', minHeight: '80px', borderRadius: '8px', padding: '8px', fontSize: '14px', border: '1px solid #d1d5db', marginTop: '0.5rem'}}
                        />
                        <div style={{fontSize: '12px', color: '#6b7280', textAlign: 'right', marginTop: '2px'}}>
                          {reflectionText.length}/200
                        </div>
                        <button
                          onClick={() => {
                            if (!user || !selectedCourse?.CRN) return;
                            if (reflectionText.trim().length >= 3) {
                              setReflectionSubmitted(true);
                              setCongratsMessage(null);
                              localStorage.removeItem(`congratsMessage_${selectedCourse?.CRN}`);
                              localStorage.setItem(`reflection_${selectedCourse?.CRN}`, 'true');
                              localStorage.setItem(`reflectionText_${selectedCourse?.CRN}`, reflectionText);
                              logEvent('reflection_submitted', reflectionText.slice(0, 200));
                              Promise.all([
                                hintService.getAllHintsByStudentAndCourse(user.studentID, selectedCourse.CRN),
                                errorsService.getAllErrorsForStudent(user.studentID, selectedCourse.CRN),
                                questionService.getCompletedProblemSets(user.studentID, selectedCourse.CRN)
                              ]).then(([hintsData, errorsData, setsData]) => {
                                localStorage.setItem(`reflectionCompletedSets_${selectedCourse.CRN}`, JSON.stringify(setsData?.completed_problem_sets || []));
                              });
                              fetchHintConceptSummary();
                              logEvent('dashboard_unlocked', 'after reflection');
                            }
                          }}
                          disabled={reflectionText.trim().length < 3}
                          style={{marginTop: '0.5rem', padding: '6px 16px', background: reflectionText.trim().length < 3 ? '#9ca3af' : '#003057', color: 'white', borderRadius: '8px', border: 'none', cursor: reflectionText.trim().length < 3 ? 'not-allowed' : 'pointer'}}
                        >
                          Explore Your Dashboard
                        </button>
                        {reflectionText.length > 0 && reflectionText.trim().length < 3 && (
                          <div style={{fontSize: '12px', color: '#dc2626', marginTop: '4px'}}>
                            Please write a bit more before continuing.
                          </div>
                        )}
                      </div>
                    </div>

                  </div>
                ) : (
                  <>
                  {errorTopics.length > 0 ? (
                      <div className="error-dial-section">
                        <h2 className="dash-section-heading"> Learn from your Error History</h2>
                        <p className="dash-section-sub">Tap a topic to see your previous incorrect submissions</p>

                        <div className="error-dial-wrap">
                          {/* Donut */}
                          <svg width="240" height="240" viewBox="0 0 240 240" style={{flexShrink: 0}}>
                            {dialSlices.map((slice, i) => (
                              <g key={i}>
                                <circle
                                  cx="120" cy="120" r="85"
                                  fill="none"
                                  stroke={slice.color}
                                  strokeWidth={selectedTopic?.topic === slice.topic ? 52 : 44}
                                  strokeDasharray={`${slice.dash} ${CIRCUMFERENCE}`}
                                  strokeDashoffset={slice.offset}
                                  style={{cursor: 'pointer', transition: 'stroke-width 0.2s ease'}}
                                  onClick={() => { openTopicPopup(slice); logEvent('error_dial_click', slice.topic); }}
                                />
                              </g>
                            ))}
                            {selectedTopic ? (
                              <>
                                <text x="120" y="110" textAnchor="middle" fontSize="26" fontWeight="700" fill="#003057">{selectedTopic.percentage}%</text>
                                <text
                                  x="120" y="130"
                                  textAnchor="middle"
                                  fontSize="13"
                                  fontWeight="600"
                                  fill="#6b7280"
                                  {...(selectedTopic.topic.length > 19 ? {textLength: "80", lengthAdjust: "spacingAndGlyphs"} : {})}
                                >
                                  {selectedTopic.topic}
                                </text>
                              </>
                            ) : (
                              <>
                                <text x="120" y="114" textAnchor="middle" fontSize="15" fontWeight="700" fill="#003057">click on</text>
                                <text x="120" y="132" textAnchor="middle" fontSize="15" fontWeight="700" fill="#003057">a slice</text>
                              </>
                            )}
                          </svg>

                          {/* Legend */}
                          <div className="error-dial-legend">
                            {dialSlices.map((slice, i) => (
                              <div
                                key={i}
                                className={`error-legend-item ${selectedTopic?.topic === slice.topic ? 'active' : ''}`}
                                onClick={() => { openTopicPopup(slice); logEvent('error_dial_click', slice.topic); }}
                              >
                                <span className="error-legend-dot" style={{background: slice.color}}></span>
                                <div className="error-legend-item-content">
                                  <span className="error-legend-name">{slice.topic}</span>
                                  <span className="error-legend-count">{slice.count} {slice.count === 1 ? 'error' : 'errors'}</span>
                                </div>
                                <span className="error-legend-pct">{slice.percentage}%</span>
                                <span className="error-legend-arrow">›</span>
                              </div>
                            ))}
                          </div>
                        </div> {/* error dial+legend section div*/}

                        {/* Popup */}
                        {showErrorPopup && selectedTopic && (
                          <div className="error-popup-overlay">
                            <div className="error-popup">
                              <div className="error-popup-head">
                                <span className="error-popup-title">
                                  {selectedTopic.topic} — {topicErrors[currentErrorIdx]?.error_type?.replace(/_errors$/, '').replace(/_/g, ' ') || 'your past mistakes'}
                                </span>
                                <button className="error-popup-close" onClick={() => { setShowErrorPopup(false); logEvent('error_popup_close', selectedTopic?.topic); }}>✕</button>
                              </div>
                              <div className="error-popup-body">
                                {topicErrors.length === 0 ? (
                                  <div className="error-popup-loading">Loading examples...</div>
                                ) : (
                                  <>
                                    <div className="error-desc">{topicErrors[currentErrorIdx]?.error_description}</div>
                                    <div className="error-code-label">your submission</div>
                                    <pre className="error-code-block">{topicErrors[currentErrorIdx]?.code}</pre>
                                    <div className="error-nav-row">
                                      <button
                                        className="error-nav-btn"
                                        disabled={currentErrorIdx === 0}
                                        onClick={() => { setCurrentErrorIdx(i => i - 1); logEvent('error_popup_nav', `prev_${selectedTopic?.topic}`); }}
                                      >← Prev</button>
                                      <span className="error-nav-ctr">{currentErrorIdx + 1} of {topicErrors.length}</span>
                                      <button
                                        className="error-nav-btn"
                                        disabled={currentErrorIdx === topicErrors.length - 1}
                                        onClick={() => { setCurrentErrorIdx(i => i + 1); logEvent('error_popup_nav', `next_${selectedTopic?.topic}`); }}
                                      >Next →</button>
                                      <Link
                                        to={`/question/${topicErrors[currentErrorIdx]?.qid}`}
                                        className="error-practice-btn"
                                        onClick={() => logEvent('error_practice_click', topicErrors[currentErrorIdx]?.function_name)}
                                      >
                                        ↺ View this problem
                                      </Link>
                                    </div>
                                  </>
                                )}
                              </div>
                            </div>
                          </div> // error popup div
                        )}
                      </div>  // full error section div (there are errors)
                  ) : (
                      <div className="error-dial-section">
                        <h2 className="dash-section-heading">Learn from your Error History</h2>
                        <p className="dash-section-sub">Tap a topic to see your previous incorrect submissions</p>
                        <div className="win-card">
                          <div className="win-emoji">📝</div>
                          <div className="win-title">No errors yet!
                          Solve more problems to view your error patterns!</div>
                        </div>
                      </div>  //no students div
                  )} {/* entire error section div*/}

                  {selectedCourse?.CRN && (
                      <div className="error-dial-section"> {/* says error dial section but actually hints*/}
                        <h2 className="dash-section-heading">Your Hint Insights</h2>
                        <p className="dash-section-sub">Personalized feedback based on how you use hints</p>

                        {recentWeightedMedian === null  && (
                          <div className="hint-card">
                            <div className="hint-concepts-emoji">
                                {recentWeightedMedian < 60 ? '🤔' : '🧠'}
                              </div>
                            <div className="win-title">Use more hints so we can calculate your hint usage patterns and give you personalized feedback.</div>
                          </div> // not enough hints used div
                        )}
                        {!(recentWeightedMedian === null ) && (
                        <>
                          <div className="hint-card" style={{marginBottom: '0.75rem', width: '100%'}}>
                            <div className="hint-card-body">
                              <div className="hint-card-header">
                                <div className="hint-card">
                                <span className="hint-concepts-emoji" style={{marginLeft: '-2rem', marginTop: '1rem'}} >{recentWeightedMedian < 60 ? '🤔' : '🧠'}</span>
                                  <div className="win-title-1" style={{marginLeft: '.5rem', marginTop: '1rem'}}>{hintMessage}</div>
                                </div>
                          </div> {/* hint history card (enough hints)*/}

                        <div style={{marginTop: '0.75rem', paddingTop: '0.75rem'}}>
                          <div className="hint-history-legend">
                          <span><span style={{color: '#16a34a', fontWeight: '700'}}>● </span>Good Reflection (1+ min)</span>
                          <span><span style={{color: '#dc2626', fontWeight: '700'}}>● </span>Rapid Hint Usage (&lt;1 min)</span>
                          </div>
                          
                          
                          {allHints.length === 0 ? (
                        <p style={{fontSize: '13px', color: '#6b7280'}}>No hint history available.</p>
                        ) : (
                          <>
                            <div style={{display: 'flex', flexDirection: 'column', gap: '1rem'}}>
                              {(showHintHistory ? hintSessions : hintSessions.slice(0, 1)).map((session, si) => (
                                <div key={si}>
                                  <div className="hint-session-header" style={{color: session.sessionColor}}>
                                    Session {hintSessions.length - si} — {session.date.toLocaleDateString('en-US', {weekday: 'short', month: 'short', day: 'numeric'})}
                                  </div>
                                  <div className="hint-session-list">
                                    {(showHintHistory ? session.hints : [...session.hints].slice(0, 3)).map((hint, hi, arr) => (
                                      <div key={hi}>
                                        {hint.gapSeconds !== null && (
                                          <div className="hint-gap-indicator" style={{color: hint.gapSeconds < 60 ? '#dc2626' : '#16a34a'}}>
                                            ↕ {hint.gapSeconds < 60
                                              ? `${hint.gapSeconds}s`
                                              : hint.gapSeconds < 300
                                              ? `${Math.floor(hint.gapSeconds / 60)}m ${hint.gapSeconds % 60}s`
                                              : `${Math.round(hint.gapSeconds / 60)}m`}
                                          </div>
                                        )}
                                        {hi >= 0 && hint.qid !== arr[hi-1]?.qid && (
                                          <div style={{borderBottom: '1px solid #6b7280', borderTop: '1px solid #6b7280', margin: '4px 0', fontSize: '13px', color: '#6b7280', marginTop: '6px', marginBottom: '2px', fontStyle: 'italic'}}>
                                            {hint.functionName}()
                                          </div>
                                        )}
                                        <div className="hint-item" style={{borderLeft: `3px solid ${session.sessionColor}`}}>
                                          <span className="hint-item-time">
                                            {new Date(hint.timestamp + 'Z').toLocaleTimeString('en-US', {hour: '2-digit', minute: '2-digit'})}
                                          </span>
                                          <p className="hint-item-desc">{hint.description}</p>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              ))}
                            </div>
                            {hintSessions.length > 1 && (
                            <button
                              className="hint-history-btn"
                              style={{marginTop: '0.75rem'}}
                              onClick={() => { 
                                setShowHintHistory(!showHintHistory); 
                                logEvent('hint_history_toggle', showHintHistory ? 'close' : 'open'); 
                              }}
                            >
                              {showHintHistory ? 'Show less' : `Show full history`}
                            </button>
                          )}
                         </>
                        )}
                          </div>
                         {/* show hint history section*/}
                        </div> 
                      </div> 
                          </>
                        )}
                      </div>// hints section
                    )}


                <div className="error-dial-section"> {/* says error dial section is actually focus areas*/}
                <div className="dash-section-heading">AI Suggested Focus Areas</div>
                          <div style={{marginTop: '1rem'}}></div>
                          <div className="hint-card">
                            <div style={{marginTop: '0.5rem', marginBottom: '0.5rem'}}>
                            {hintSummaryLoading
                                ?
                                <span className="hint-message">🤖 Analyzing your previous submissions...</span>
                                : hintConceptSummary
                                  ? <>
                                      <span className="hint-message-1"
                                        style={{whiteSpace: 'pre-line'}}
                                        dangerouslySetInnerHTML={{__html: hintConceptSummary
                                          ?.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                                          ?.replace(/^(\d+\.)\s/gm, '<strong>$1</strong> ')
                                        }}
                                      />
                                    <div style={{
                                      marginTop: '1.25rem',
                                      borderTop: '1px solid #e5e7eb',
                                      paddingTop: '1.25rem',
                                    }}>
                                      {!summaryRating ? (
                                        <div style={{
                                          background: '#f3f0fa',
                                          borderRadius: '12px',
                                          padding: '1.5rem',
                                        }}>
                                          <div style={{display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '1.25rem'}}>
                                            <div style={{
                                              width: '48px', height: '48px', borderRadius: '50%',
                                              background: '#6d4aff', display: 'flex', alignItems: 'center',
                                              justifyContent: 'center', flexShrink: 0, fontSize: '24px'
                                            }}>
                                              🤖
                                            </div>
                                            <div>
                                              <p style={{fontSize: '22px', fontWeight: '600', margin: 0, color: '#003057'}}>
                                                Chat with your AI Learning Coach
                                              </p>
                                              <p style={{fontSize: '17px', color: 'black', margin: '4px 0 0'}}>
                                                Does this match what you're struggling with?
                                              </p>
                                            </div>
                                          </div>

                                      <div style={{display: 'flex', gap: '12px'}}>
                                        <button
                                          onMouseEnter={() => setHoveredButton('up')}
                                          onClick={() => { setSummaryRating('up'); logEvent('summary_rating', 'thumbs_up'); localStorage.setItem(`summaryRating_${selectedCourse?.CRN}`, 'up'); }}
                                          style={{
                                            ...buttonBaseStyle,
                                            background: hoveredButton === 'up' ? '#6d4aff' : 'white',
                                            color: hoveredButton === 'up' ? 'white' : '#003057',
                                            border: hoveredButton === 'up' ? 'none' : '1px solid #d1d5db',
                                          }}
                                        >
                                          👍 Yes, that's it
                                        </button>
                                        <button
                                          onMouseEnter={() => setHoveredButton('down')}
                                          onClick={() => { setSummaryRating('down'); logEvent('summary_rating', 'thumbs_down'); localStorage.setItem(`summaryRating_${selectedCourse?.CRN}`, 'down'); }}
                                          style={{
                                            ...buttonBaseStyle,
                                            background: hoveredButton === 'down' ? '#6d4aff' : 'white',
                                            color: hoveredButton === 'down' ? 'white' : '#003057',
                                            border: hoveredButton === 'down' ? 'none' : '1px solid #d1d5db',
                                          }}
                                        >
                                          👎 Not quite
                                        </button>
                                      </div>
                                          <p style={{fontSize: '15px', color: '#003057', textAlign: 'center', margin: '14px 0 0'}}>
                                            Either answer starts our conversation
                                          </p>
                                        </div>
                                      ) : (
                                        <div style={{
                                          background: '#f3f0fa',
                                          borderRadius: '12px',
                                          padding: '1.25rem',
                                        }}>
                                          <DashboardChatbot
                                            summaryRating={summaryRating}
                                            hintConceptSummary={hintConceptSummary}
                                            reflectionText={reflectionText}
                                            errorTopics={errorTopics}
                                            studentId={user?.studentID}
                                            courseId={selectedCourse?.CRN}
                                            logEvent={logEvent}
                                          />
                                        </div>
                                      )}
                                    </div>
                                    </>
                                  : <span className="win-title">🤖 Solve more problems to generate feedback based on your patterns.</span>
                                }
                                </div>
                              </div>
                            </div>{/* hint concept summary card*/}

                <div className="error-dial-section"> {/* says error dial section is actually what's next*/}
                    <h2 className="dash-section-heading">What's Next?</h2>
                    <p className="dash-section-sub">Problems worth revisiting based on your attempt history</p>

                    <div style={{display: 'flex', flexDirection: 'column', gap: '0.75rem'}}>
                    {incompleteProblems.length === 0 && courseCompletedCount === 0 ? (
                          <div className="win-card">
                            <div className="win-emoji">🚀</div>
                            <div className="win-title">Ready to start?</div>
                            <div className="win-desc">Submit your first solution to start tracking your progress here.</div>
                          </div>
                        ) : incompleteProblems.length === 0 ? (
                        <div className="win-card">
                          <div className="win-emoji">🎉</div>
                          <div className="win-title">You're all caught up!</div>
                          <div className="win-desc">No incomplete problems — keep solving to build your progress.</div>
                        </div>
                      ) : (
                      incompleteProblems.map((problem, i) => (
                        <div key={i} className="win-card" style={{display: 'flex', alignItems: 'center', gap: '1rem', padding: '0.75rem 1rem'}}>
                          <div style={{
                              background: 'white',
                              borderRadius: '8px',
                              padding: '6px 10px',
                              textAlign: 'center',
                              minWidth: '52px',
                              flexShrink: 0
                            }}>
                              <div style={{
                                fontSize: '1.2rem',
                                fontWeight: '700',
                                color: '#003057'
                              }}>{problem.attempts}</div>
                              <div style={{fontSize: '13px', color: '#003057'}}>{problem.attempts === 1 ? 'attempt' : 'attempts'}</div>
                            </div>
                          <div style={{flex: 1}}>
                            <div className="win-title">{problem.functionName}()</div>
                            <div className="win-desc">Last tried {problem.lastTried}</div>
                          </div>
                          {i===0 && (
                            <div style={{fontSize: '14px', color: '#003057', marginTop: '3px', marginRight: '5rem'}}>
                              
                            </div>
                          )}
                          {i === 0 && (
                                <div style={{fontSize: '16px', color: '#003057', marginTop: '3px', marginRight: '3px'}}>
                                  💡Stuck? Try using a hint, or visit office hours to ask a TA.
                                </div>
                            )}
                          <Link 
                              to={`/question/${problem.qid}`} 
                              className="error-practice-btn" 
                              style={{marginRight: "2rem"}}
                              onClick={() => logEvent('do_next_click', problem.functionName)}
                            >
                              Try again ↺
                            </Link>
                        </div>
                      )))}
                    </div>
                </div> {/* what's next section */}
                </>
              )}
                  </div> {/* 'student dashboard' section below stat cards*/}



              {/* Join Course Modal */}
              <Modal show={showJoinModal} onHide={closeJoinModal}>
                <Modal.Header closeButton={false}>
                  <Modal.Title>Join a Course</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                  <form onSubmit={handleJoinCourse}>
                    <div className="mb-3">
                      <label htmlFor="joinCode" className="form-label">Course Code</label>
                      <input
                        type="text"
                        className="form-control"
                        id="joinCode"
                        value={joinCode}
                        onChange={e => setJoinCode(e.target.value.toUpperCase())}
                        style={{ textTransform: 'uppercase' }}
                        required
                        autoFocus
                      />
                    </div>
                    {joinError && <div className="text-danger mb-2">{joinError}</div>}
                    <Button variant="primary" type="submit" disabled={joinLoading || !user?.gtid} className="quick-action-button w-100">
                      {joinLoading ? 'Joining...' : 'Join'}
                    </Button>
                  </form>
                </Modal.Body>
              </Modal>

              {/* View Enrolled Courses Modal */}
              <Modal show={showCoursesModal} onHide={() => setShowCoursesModal(false)}>
                <Modal.Header>
                  <Modal.Title>Enrolled Courses</Modal.Title>
                  <Button
                    variant="link"
                    onClick={() => setShowCoursesModal(false)}
                    className="modal-close-button"
                    aria-label="Close"
                  >
                    &times;
                  </Button>
                </Modal.Header>
                <Modal.Body>
                  {Array.isArray(courses) && courseCount > 0 ? (
                    <ul className="course-list">
                      {courses.map((course, idx) => {
                        const courseId = course.CRN || course.id || course.crn || course._id || idx;
                        const courseName = course.name || course.title || course.courseName || course.course_name || '';
                        const courseSemester = course.semester || course.term || '';
                        const isSelected = selectedCourse && (courseId === selectedCourse || courseName === selectedCourse || course === selectedCourse);
                        return (
                          <li key={courseId} className={`course-list-item ${isSelected ? 'selected' : ''}`}>
                            <div className="course-list-name">{courseName}</div>
                            {courseSemester && (
                              <div className="course-list-semester">Semester: {courseSemester}</div>
                            )}
                            {isSelected && <span className="course-list-current">(Current)</span>}
                          </li>
                        );
                      })}
                    </ul>
                  ) : (
                    <div>No enrolled courses found.</div>
                  )}
                </Modal.Body>
              </Modal>
            </div> {/* student dashboard container div*/}
        </div> {/* student dashboard content div*/}
      </div> {/* student dashboard div*/}
    </>
  );
}